import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps, renderSlot as _renderSlot, isRef as _isRef, normalizeClass as _normalizeClass, withCtx as _withCtx, renderList as _renderList, createSlots as _createSlots, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { isEmptyOrNullString } from '@packages/toolbox/dist/utils/validators';
import { QSelect } from 'quasar';
import { onMounted, ref } from 'vue';

import SSelect from 'SharedComponents/ui/Select/SSelect.vue';
import { ValidationRule } from 'SharedDir/common/composables/useValidation';
import { EmitValue, PropValue, useValue } from 'SharedDir/common/composables/useValue';
import { Dadata } from 'SharedDir/declarations/dadata';
import getSuggestions from 'SharedDir/service/dadata/field-address-dadata';
import { resolveService } from 'SharedDir/utils/vue';

/**
 * Документация DaData
 * https://dadata.ru/api/suggest/address/
 */
interface DadataProps extends PropValue<null | Dadata.FieldAddress> {
  // apiKey: string;
  rules?: ValidationRule[];
  disable?: boolean;
  placeholder?: string;
  readonly?: boolean;
  fromBound?: Dadata.BoundsType;
  toBound?: Dadata.BoundsType;
  worldWide?: boolean;
  isRequired?: boolean;
  errorMessage?: string;
  label?: string;
}

export default /*@__PURE__*/_defineComponent({
  ...{ name: 'FieldAddressDadata' },
  __name: 'FieldAddressDadata',
  props: {
    rules: {},
    disable: { type: Boolean, default: false },
    placeholder: { default: '' },
    readonly: { type: Boolean, default: false },
    fromBound: { default: 'region' },
    toBound: { default: 'street' },
    worldWide: { type: Boolean, default: false },
    isRequired: { type: Boolean, default: false },
    errorMessage: {},
    label: { default: '' },
    modelValue: {}
  },
  emits: ["update:modelValue"],
  setup(__props: any, { expose: __expose, emit: __emit }) {



const collectValue = (val: Dadata.DadataSuggestion): Dadata.FieldAddress => {
  const isCity = !!val.data?.city;

  return {
    value: val.value,
    unrestrictedValue: val.unrestricted_value,
    kladrId: val.data.kladr_id,
    fiasId: val.data.fias_id,
    isCity,
    postalCode: val.data.postal_code || '',
    country: val.data.country || '',
    countryIsoCode: val.data.country_iso_code || '',
    region: val.data.region || '',
    area: val.data.area || '',
    city: val.data.city || '',
    settlement: val.data.settlement || '',
    street: val.data.street || '',
    house: val.data.house || '',
    block: val.data.block || '',
    flat: val.data.flat || '',
    geoLat: val.data.geo_lat || '',
    geoLon: val.data.geo_lon || '',
    fiasLevel: val.data.fias_level || '',
    okato: val.data.okato || '',
    oktmo: val.data.oktmo || '',
    taxOffice: val.data.tax_office || '',
    taxOfficeLegal: val.data.tax_office_legal || '',
  };
};

const props = __props;
const emit = __emit;
const { innerValue, emitValue } = useValue<
  Dadata.FieldAddress | null,
  Dadata.DadataSuggestion | null
>(props, emit, {
  mrofsnart: (value) => (value ? collectValue(value) : null),
});

const loading = ref(false);
const addressSuggestions = ref<Dadata.DadataSuggestion[]>([]);
const select = ref(null as unknown as QSelect);

const token = resolveService('DADATA_API_KEY');

const selectOptionLabel = (val: Dadata.DadataSuggestion) => val.value;
const selectOptionValue = (val: Dadata.DadataSuggestion) => val;

const updateAddresses = async (query: string) => {
  const bounds = {
    fromBound: { value: props.fromBound },
    toBound: { value: props.toBound },
  };

  const config = { query, token, ...bounds } as Dadata.Suggestion;
  if (props.worldWide) {
    config.locations = [{ country_iso_code: '*' }];
  }
  const {
    data: { suggestions },
  } = await getSuggestions(config);
  addressSuggestions.value = suggestions;
};

const filterAddress = async (inputValue: string, update, abort) => {
  if (isEmptyOrNullString(inputValue)) {
    abort();
    return;
  }
  try {
    await updateAddresses(inputValue);
    update();
  } catch (e) {
    console.error(e);
    abort();
  }
};

const validate = () => select.value.validate();

const fetchAnyDadata = async () => {
  if (
    innerValue.value?.value &&
    !isEmptyOrNullString(innerValue.value?.value) &&
    !innerValue.value?.unrestricted_value
  ) {
    await updateAddresses(innerValue.value?.value);
    if (addressSuggestions.value.length > 0) {
      innerValue.value = { ...addressSuggestions.value.at(0), ...innerValue.value };
      emitValue();
    }
  }
};

__expose({ validate });

onMounted(fetchAnyDadata);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(SSelect, {
    ref_key: "select",
    ref: select,
    modelValue: _unref(innerValue),
    "onUpdate:modelValue": [
      _cache[0] || (_cache[0] = ($event: any) => (_isRef(innerValue) ? (innerValue).value = $event : null)),
      _unref(emitValue)
    ],
    outlined: "",
    dense: "",
    "options-dense": "",
    options: addressSuggestions.value,
    "option-label": selectOptionLabel,
    "option-value": selectOptionValue,
    "map-options": "",
    "hide-bottom-space": "",
    required: "",
    "use-input": "",
    "display-value": "",
    multiple: false,
    clearable: "",
    autogrow: "",
    placeholder: _ctx.placeholder,
    "input-debounce": 500,
    "error-message": _ctx.errorMessage,
    error: !!_ctx.errorMessage,
    label: _ctx.label,
    class: _normalizeClass({ required: _ctx.isRequired }),
    disable: _ctx.disable,
    readonly: _ctx.readonly,
    rules: _ctx.rules,
    "hide-selected": !_ctx.readonly,
    "fill-input": !_ctx.readonly,
    autocomplete: "new-address",
    onFilter: filterAddress
  }, _createSlots({ _: 2 }, [
    _renderList(_ctx.$slots, (_, name) => {
      return {
        name: name,
        fn: _withCtx((slotData) => [
          _renderSlot(_ctx.$slots, name, _normalizeProps(_guardReactiveProps(slotData || {})))
        ])
      }
    })
  ]), 1032, ["modelValue", "options", "placeholder", "error-message", "error", "label", "class", "disable", "readonly", "rules", "hide-selected", "fill-input", "onUpdate:modelValue"]))
}
}

})